/**
 * Leads Page - Lead tracking and analytics
 *
 * Shows:
 * - Summary cards (Total Leads, Applicants, Opt-ins) with animated numbers
 * - Funnel Breakdown (Applicant vs Opt-in pie chart)
 * - Lead Source Breakdown pie chart
 * - Country breakdown pie chart
 * - Total Leads over time trend chart
 * - Filterable and sortable leads table with server-side pagination
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, useSpring, useTransform, useInView, AnimatePresence } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import { ResponsivePie } from "@nivo/pie";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  LabelList,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { format } from "date-fns";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";
import { FilterBar, StatCardSkeleton } from "../components";
import { DataTable, type ColumnDef } from "../components/ui/data-table";
import { branding } from "../config/branding";

// ============================================================================
// TYPES
// ============================================================================

interface Lead {
  _id: string;
  type: "applicant" | "optin";
  name: string;
  email: string;
  phone: string;
  country: string;
  countryCode: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  createdAt: string;
  responseType: string;
}

interface ChartDataItem {
  name: string;
  value: number;
}

interface DailyTrend {
  _id: string;
  applicants: number;
  optins: number;
  dmBookings: number;
  total: number;
}

interface Totals {
  total: number;
  applicants: number;
  optins: number;
  dmBookings: number;
}

interface ContentOption {
  value: string;
  label: string;
}

interface InvestmentCloseRate {
  bucket: string;
  total: number;
  closed: number;
  calledTotal: number;
  rate: number;
  callCloseRate: number;
}

interface FunnelSubmissionItem {
  funnel: string;
  full: number;
  partial: number;
  total: number;
}

interface LeadsDataSummary {
  totals: Totals;
  previousTotals: Totals | null;
  countryBreakdown: ChartDataItem[];
  sourceBreakdown: ChartDataItem[];
  funnelBreakdown: ChartDataItem[];
  funnelSourceBreakdown: ChartDataItem[];
  termBreakdown: ChartDataItem[];
  responseTypeBreakdown: ChartDataItem[];
  funnelSubmissionBreakdown: FunnelSubmissionItem[];
  applicantIncomeBreakdown: ChartDataItem[];
  investmentCloseRates: InvestmentCloseRate[];
  overallCallRate: number;
  overallCallCount: number;
  overallCloseRate: number;
  overallCloseCount: number;
  callRateTrend: ChartDataItem[];
  closeRateTrend: ChartDataItem[];
  availableLeadTypes: string[];
  dailyTrend: DailyTrend[];
  leads: Lead[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  filters: {
    sources: (string | ContentOption)[];
    funnels: (string | ContentOption)[];
    contents: (string | ContentOption)[];
    campaigns: string[];
    countries: (string | ContentOption)[];
    closers: string[];
    setters: string[];
  };
}

// ============================================================================
// CONSTANTS & UTILITIES
// ============================================================================

const PIE_COLORS = branding.colors.chart;


const formatNumber = (value: number) => value.toLocaleString();

// Country name to code mapping (reverse of what's in the API)
const countryNameToCode: Record<string, string> = {
  "USA/Canada": "1",
  "Russia": "7",
  "Egypt": "20",
  "South Africa": "27",
  "Greece": "30",
  "Netherlands": "31",
  "Belgium": "32",
  "France": "33",
  "Spain": "34",
  "Hungary": "36",
  "Italy": "39",
  "Romania": "40",
  "Switzerland": "41",
  "Austria": "43",
  "UK": "44",
  "Denmark": "45",
  "Sweden": "46",
  "Norway": "47",
  "Poland": "48",
  "Germany": "49",
  "Peru": "51",
  "Mexico": "52",
  "Cuba": "53",
  "Argentina": "54",
  "Brazil": "55",
  "Chile": "56",
  "Colombia": "57",
  "Venezuela": "58",
  "Malaysia": "60",
  "Australia": "61",
  "Indonesia": "62",
  "Philippines": "63",
  "New Zealand": "64",
  "Singapore": "65",
  "Thailand": "66",
  "Japan": "81",
  "South Korea": "82",
  "Vietnam": "84",
  "China": "86",
  "Turkey": "90",
  "India": "91",
  "Pakistan": "92",
  "Afghanistan": "93",
  "Sri Lanka": "94",
  "Myanmar": "95",
  "Iran": "98",
  "Morocco": "212",
  "Algeria": "213",
  "Tunisia": "216",
  "Libya": "218",
  "Gambia": "220",
  "Senegal": "221",
  "Nigeria": "234",
  "Kenya": "254",
  "Tanzania": "255",
  "Uganda": "256",
  "Zambia": "260",
  "Zimbabwe": "263",
  "Gibraltar": "350",
  "Portugal": "351",
  "Luxembourg": "352",
  "Ireland": "353",
  "Iceland": "354",
  "Albania": "355",
  "Malta": "356",
  "Cyprus": "357",
  "Finland": "358",
  "Bulgaria": "359",
  "Lithuania": "370",
  "Latvia": "371",
  "Estonia": "372",
  "Moldova": "373",
  "Armenia": "374",
  "Belarus": "375",
  "Ukraine": "380",
  "Serbia": "381",
  "Croatia": "385",
  "Slovenia": "386",
  "Bosnia": "387",
  "North Macedonia": "389",
  "Czech Republic": "420",
  "Slovakia": "421",
  "Hong Kong": "852",
  "Macau": "853",
  "Cambodia": "855",
  "Laos": "856",
  "Bangladesh": "880",
  "Taiwan": "886",
  "Maldives": "960",
  "Lebanon": "961",
  "Jordan": "962",
  "Syria": "963",
  "Iraq": "964",
  "Kuwait": "965",
  "Saudi Arabia": "966",
  "Yemen": "967",
  "Oman": "968",
  "UAE": "971",
  "Israel": "972",
  "Bahrain": "973",
  "Qatar": "974",
  "Bhutan": "975",
  "Mongolia": "976",
  "Nepal": "977",
  "Tajikistan": "992",
  "Turkmenistan": "993",
  "Azerbaijan": "994",
  "Georgia": "995",
  "Kyrgyzstan": "996",
  "Uzbekistan": "998",
};

// Reverse mapping: code to name
const countryCodeToName: Record<string, string> = Object.fromEntries(
  Object.entries(countryNameToCode).map(([name, code]) => [code, name])
);

// Nivo theme for charts
const getNivoPieTheme = () => ({
  labels: { text: { fontSize: 12, fontWeight: 600 } },
  legends: { text: { fontSize: 11 } },
});

// Custom tooltip for pie charts
const PieChartTooltip = ({ datum }: { datum: { id: string | number; value: number; color: string; data: { percentage: number } } }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9, y: 5 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    className="bg-card/95 backdrop-blur-md text-foreground border border-border/50 rounded-xl shadow-2xl px-4 py-3 text-sm"
  >
    <div className="flex items-center gap-2.5">
      <div
        className="w-3.5 h-3.5 rounded-full shadow-inner"
        style={{ backgroundColor: datum.color, boxShadow: `0 0 8px ${datum.color}40` }}
      />
      <span className="font-semibold">{datum.id}</span>
    </div>
    <div className="mt-1.5 text-muted-foreground font-medium">
      {datum.value.toLocaleString()} <span className="text-foreground/60">({datum.data.percentage.toFixed(1)}%)</span>
    </div>
  </motion.div>
);

// Truncate long labels for pie charts
const truncateLabel = (label: string, maxLength: number = 18): string => {
  if (label.length <= maxLength) return label;
  return label.substring(0, maxLength - 2) + "…";
};

// Transform data for Nivo pie chart
const toNivoPieData = (data: ChartDataItem[], colors: string[]) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const merged = data.map((item, index) => ({
    id: item.name,
    label: item.name,
    value: item.value,
    color: colors[index % colors.length],
    percentage: total > 0 ? (item.value / total) * 100 : 0,
  }));
  merged.sort((a, b) => {
    if (b.value !== a.value) return b.value - a.value;
    return (a.label || "").localeCompare(b.label || "");
  });
  return merged;
};

// ============================================================================
// ANIMATED NUMBER COMPONENT
// ============================================================================

// Animated number - only animates on initial load, not filter changes
function AnimatedNumber({ value, className }: { value: number; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const hasAnimatedOnce = useRef(false);

  const spring = useSpring(0, {
    stiffness: 50,
    damping: 20,
    duration: 1.2,
  });

  const display = useTransform(spring, (current) => Math.round(current).toLocaleString());
  const [displayValue, setDisplayValue] = useState("0");

  // Only animate the first time the component becomes visible
  useEffect(() => {
    if (isInView && !hasAnimatedOnce.current) {
      hasAnimatedOnce.current = true;
      spring.set(value);
    }
  }, [isInView, value, spring]);

  useEffect(() => {
    const unsubscribe = display.on("change", (v) => setDisplayValue(v));
    return unsubscribe;
  }, [display]);

  // When value changes after initial animation, update instantly (no animation)
  useEffect(() => {
    if (hasAnimatedOnce.current) {
      spring.jump(value);
    }
  }, [value, spring]);

  return (
    <span ref={ref} className={className}>
      {displayValue}
    </span>
  );
}

// ============================================================================
// ICONS
// ============================================================================

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const UserPlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
  </svg>
);

const InboxIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

// Custom tooltip for Recharts line chart
interface ChartTooltipPayloadItem {
  value: number;
  name: string;
  color: string;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: ChartTooltipPayloadItem[];
  label?: string;
}

const CustomChartTooltip = ({ active, payload, label }: ChartTooltipProps) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 5, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      className="bg-card/95 backdrop-blur-md border border-border/50 rounded-xl shadow-2xl px-4 py-3 text-sm"
    >
      <div className="font-semibold text-foreground">{label}</div>
      <div className="text-muted-foreground mt-1">
        <span className="text-red-400 font-bold">{payload[0]?.value?.toLocaleString()}</span> leads
      </div>
    </motion.div>
  );
};

// ============================================================================
// METRIC CARD COMPONENT (Premium version with glow effects)
// ============================================================================

interface LeadMetricCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: "brand" | "emerald" | "blue";
  index: number;
  percentChange?: number | null;
}

// Helper to convert hex color to RGB values
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result?.[1] || !result[2] || !result[3]) return "0,0,0";
  return `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`;
}

// Get RGB values from branding colors
const brandRgb = hexToRgb(branding.colors.primary);

const colorConfig = {
  brand: {
    text: "text-[var(--brand-primary)]",
    iconBg: "bg-[var(--brand-primary)]/15",
    stroke: branding.colors.primary,
    rgb: brandRgb,
  },
  emerald: {
    text: "text-emerald-400",
    iconBg: "bg-emerald-500/15",
    stroke: "#10b981",
    rgb: "16,185,129",
  },
  blue: {
    text: "text-sky-400",
    iconBg: "bg-sky-500/15",
    stroke: "#38BDF8",
    rgb: "56,189,248",
  },
};

function LeadMetricCard({ label, value, icon, color, index, percentChange }: LeadMetricCardProps) {
  const { theme: appTheme } = useTheme();
  const isCrimson = appTheme === "crimson";
  const isDark = appTheme === "dark";
  const theme = colorConfig[color];
  const [isHovered, setIsHovered] = useState(false);

  const cardStyle = isCrimson ? {
    background: "white",
    borderColor: "rgba(0,0,0,0.08)",
    boxShadow: isHovered ? "0 8px 30px -8px rgba(0,0,0,0.2)" : "0 2px 16px -4px rgba(0,0,0,0.1)",
  } : isDark ? {
    background: `linear-gradient(to bottom right, rgba(${theme.rgb}, 0.38), rgba(${theme.rgb}, 0.22), rgba(${theme.rgb}, 0.06))`,
    borderColor: `rgba(${theme.rgb}, 0.45)`,
    boxShadow: isHovered ? `0 0 50px -5px rgba(${theme.rgb},0.5)` : `0 0 35px -5px rgba(${theme.rgb},0.35)`,
  } : {
    background: `linear-gradient(to bottom right, rgba(${theme.rgb}, 0.2), rgba(${theme.rgb}, 0.1), transparent)`,
    borderColor: `rgba(${theme.rgb}, 0.25)`,
    boxShadow: isHovered ? `0 0 40px -5px rgba(${theme.rgb},0.4)` : `0 0 25px -5px rgba(${theme.rgb},0.2)`,
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.5,
        delay: 0.1 + index * 0.1,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      whileHover={{
        y: -4,
        transition: { duration: 0.2 },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative overflow-hidden border rounded-2xl p-6 transition-shadow duration-500 ease-out backdrop-blur-sm"
      style={cardStyle}
    >
      {/* Subtle animated gradient overlay */}
      <motion.div
        className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500"
        style={{
          background: `radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), ${theme.stroke}15, transparent 50%)`,
        }}
      />

      {/* Noise texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative flex items-start justify-between">
        <div className="space-y-3 flex-1">
          <motion.div
            className="flex items-center gap-3"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 + index * 0.1 }}
          >
            <motion.div
              className={`p-2 rounded-xl ${theme.iconBg} ${theme.text}`}
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              {icon}
            </motion.div>
            <span className="text-xs md:text-sm font-semibold text-foreground/80 uppercase tracking-wider">
              {label}
            </span>
          </motion.div>
          <div className="flex items-baseline gap-2 md:gap-3">
            <AnimatedNumber
              value={value}
              className="text-2xl md:text-4xl font-bold text-foreground tracking-tight"
            />
            {percentChange != null && (
              <motion.span
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className={`text-sm font-semibold ${
                  percentChange > 0
                    ? "text-emerald-400"
                    : percentChange < 0
                    ? "text-red-400"
                    : "text-muted-foreground"
                }`}
              >
                {percentChange > 0 ? "+" : ""}
                {percentChange.toFixed(2)}%
              </motion.span>
            )}
          </div>
        </div>

        {/* Decorative element */}
        <motion.div
          className={`absolute -right-4 -bottom-4 w-24 h-24 rounded-full ${theme.iconBg} opacity-50 blur-2xl`}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>
    </motion.div>
  );
}

// ============================================================================
// SUMMARY CARDS COMPONENT
// ============================================================================

// DM Booking icon (chat bubble)
const DMIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

function LeadsSummaryCards({ totals, previousTotals, isLoading }: { totals: Totals | null; previousTotals: Totals | null; isLoading: boolean }) {
  // Only show skeletons on initial load (no data yet), not during filter changes
  if (isLoading && !totals) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <StatCardSkeleton key={i} index={i} />
        ))}
      </div>
    );
  }

  if (!totals) return null;

  // Calculate percentage change: ((current - previous) / previous) * 100
  const calcPercentChange = (current: number, previous: number): number | null => {
    if (!previousTotals) return null;
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      <LeadMetricCard
        label="Total Leads"
        value={totals.total}
        icon={<UsersIcon />}
        color="brand"
        index={0}
        percentChange={calcPercentChange(totals.total, previousTotals?.total ?? 0)}
      />
      <LeadMetricCard
        label="Applicants"
        value={totals.applicants}
        icon={<UserPlusIcon />}
        color="emerald"
        index={1}
        percentChange={calcPercentChange(totals.applicants, previousTotals?.applicants ?? 0)}
      />
      <LeadMetricCard
        label="Opt-ins"
        value={totals.optins}
        icon={<InboxIcon />}
        color="blue"
        index={2}
        percentChange={calcPercentChange(totals.optins, previousTotals?.optins ?? 0)}
      />
      <LeadMetricCard
        label="DM Bookings"
        value={totals.dmBookings || 0}
        icon={<DMIcon />}
        color="brand"
        index={3}
        percentChange={calcPercentChange(totals.dmBookings || 0, previousTotals?.dmBookings ?? 0)}
      />
    </div>
  );
}

// ============================================================================
// INVESTMENT CLOSE RATE GAUGE
// ============================================================================

function InvestmentCloseRateGauge({ bucket, total, closed, rate, sublabel = "closed", statLabel, statValue, totalLabel = "leads" }: { bucket: string; total: number; closed: number; rate: number; sublabel?: string; statLabel?: string; statValue?: number; totalLabel?: string }) {
  const size = 160;
  const strokeWidth = 14;
  const r = (size - strokeWidth) / 2 - 4;
  const cx = size / 2;
  const cy = size / 2;

  function pt(angleDeg: number): [number, number] {
    const rad = (angleDeg * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  }

  // Gauge sweeps from 135° (lower-left) clockwise 270° to 45° (lower-right), passing through top
  const startAngle = 135;
  const totalSweep = 270;
  const [sx, sy] = pt(startAngle);
  const [ex, ey] = pt(45);

  // Track: large arc (270°), clockwise
  const trackD = `M ${sx.toFixed(2)} ${sy.toFixed(2)} A ${r} ${r} 0 1 1 ${ex.toFixed(2)} ${ey.toFixed(2)}`;

  // Fill arc based on rate
  let fillD = "";
  if (rate > 0) {
    const fillAngle = startAngle + (totalSweep * rate) / 100;
    const [fx, fy] = pt(fillAngle);
    const fillSweep = (totalSweep * rate) / 100;
    const largeArc = fillSweep > 180 ? 1 : 0;
    fillD = `M ${sx.toFixed(2)} ${sy.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${fx.toFixed(2)} ${fy.toFixed(2)}`;
  }

  // Color: use brand chart colors tiered by rate
  const fillColor = rate >= 50
    ? branding.colors.chart[0]
    : rate >= 25
    ? branding.colors.chart[2]
    : branding.colors.chart[4];

  // Shorten bucket label for display
  const shortLabel = bucket
    .replace(" (We cannot help you)", "")
    .replace("$0-$500", "$0-$500")
    .replace("$0-$1000", "$0-$1k");

  return (
    <div className="flex flex-col items-center rounded-xl border bg-card p-4 gap-1">
      <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest text-center leading-tight mb-1">
        {shortLabel}
      </div>
      <svg
        width={size}
        height={Math.round(size * 0.72)}
        viewBox={`0 0 ${size} ${size}`}
        style={{ overflow: "visible" }}
      >
        {/* Track */}
        <path
          d={trackD}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          opacity={0.1}
        />
        {/* Fill */}
        {rate > 0 && (
          <path
            d={fillD}
            fill="none"
            stroke={fillColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
        )}
        {/* Percentage */}
        <text
          x={cx}
          y={cy + 8}
          textAnchor="middle"
          fontSize="26"
          fontWeight="800"
          fill="currentColor"
        >
          {rate}%
        </text>
        <text
          x={cx}
          y={cy + 26}
          textAnchor="middle"
          fontSize="10"
          fill="currentColor"
          opacity={0.5}
        >
          {sublabel}
        </text>
      </svg>
      <div className="flex gap-3 text-xs text-muted-foreground mt-1">
        <span>
          <span className="font-bold text-foreground">{total.toLocaleString()}</span> {totalLabel}
        </span>
        <span>
          <span className="font-bold" style={{ color: fillColor }}>{closed.toLocaleString()}</span> closed
        </span>
      </div>
      {statLabel !== undefined && statValue !== undefined && (
        <div className="text-[10px] text-muted-foreground/60 mt-0.5">
          {statValue.toLocaleString()} {statLabel}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CHART CARD WRAPPER
// ============================================================================

interface ChartCardProps {
  title: string;
  children: React.ReactNode;
  delay?: number;
  height?: string;
  activeFilters?: string[];
  onClearFilters?: () => void;
}

function ChartCard({ title, children, delay = 0.4, height = "h-[400px]", activeFilters, onClearFilters }: ChartCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      whileHover={{
        y: -2,
        boxShadow: "0 20px 60px -15px rgba(0, 0, 0, 0.15)",
        transition: { duration: 0.3 },
      }}
      className="relative bg-card rounded-2xl border border-border p-6 overflow-hidden transition-all duration-300 hover:border-border/60"
    >
      {/* Subtle gradient overlay on hover */}
      <div className="absolute inset-0 bg-linear-to-br from-white/2 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      <div className="flex items-center justify-between mb-4">
        <motion.h3
          className="text-lg font-bold tracking-tight"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: delay + 0.1 }}
        >
          {title}
        </motion.h3>
        <AnimatePresence>
          {activeFilters && activeFilters.length > 0 && onClearFilters && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={onClearFilters}
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
            >
              <span>Clear: {activeFilters.length > 2 ? `${activeFilters.slice(0, 2).join(", ")}...` : activeFilters.join(", ")}</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
      <div className={`${height} cursor-pointer`}>
        {children}
      </div>
    </motion.div>
  );
}

// ============================================================================
// LEAD TIMELINE DRAWER
// ============================================================================

interface TimelineOptin {
  date: string; source: string; medium: string; campaign: string; content: string; sheet: string;
}
interface TimelineApplication {
  date: string; source: string; medium: string; campaign: string; content: string; responseType: string;
}
interface TimelineCall {
  date: string; outcome: string; closer: string; setter: string; cash: number; offer: string;
  notes: string; fathomLink: string; platform: string; funnel: string;
}
interface TimelineWebinarOptin {
  date: string; webinarName: string; webinarId: string; source: string; medium: string; campaign: string;
}
interface TimelineMeetingBooking {
  bookedAt: string; meetingAt: string; closer: string;
}
interface LeadTimelineData {
  email: string; name: string; phone: string; country: string; leadType: string;
  profile: { hasBrand: string; currentMRR: string; ableToInvest: string };
  optins: TimelineOptin[];
  application: TimelineApplication | null;
  calls: TimelineCall[];
  webinarOptins: TimelineWebinarOptin[];
  meetingBookings: TimelineMeetingBooking[];
  totalRevenue: number;
  isClosed: boolean;
}

function fmtDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function OutcomeBadge({ outcome }: { outcome: string }) {
  const closed = new Set(["Closed", "closed", "Close", "close", "Won", "won", "Sale", "sale"]);
  const noshow = new Set(["No Show", "Cancelled", "Rescheduled", "Cancelled by sales team"]);
  const isWon = closed.has(outcome);
  const isNoShow = noshow.has(outcome) || !outcome;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
      isWon ? "bg-emerald-500/15 text-emerald-400" :
      isNoShow ? "bg-zinc-500/15 text-zinc-400" :
      "bg-amber-500/15 text-amber-400"
    }`}>
      {outcome || "No Show"}
    </span>
  );
}

function LeadTimelineDrawer({ email, onClose }: { email: string; onClose: () => void }) {
  const [data, setData] = useState<LeadTimelineData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setData(null);
    fetch(`/api/lead-timeline?email=${encodeURIComponent(email)}`)
      .then(r => r.json() as Promise<LeadTimelineData>)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [email]);

  const pri = branding.colors.primary;
  const sec = branding.colors.secondary;

  // Build sorted timeline events
  const events = data ? [
    ...data.optins.map(o => ({ type: "optin" as const, date: o.date, data: o })),
    ...(data.webinarOptins ?? []).map(w => ({ type: "webinar" as const, date: w.date, data: w })),
    ...(data.meetingBookings ?? []).map(m => ({ type: "meeting" as const, date: m.bookedAt, data: m })),
    ...(data.application ? [{ type: "app" as const, date: data.application.date, data: data.application }] : []),
    ...data.calls.map(c => ({ type: "call" as const, date: c.date, data: c })),
  ].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return -1;
    if (!b.date) return 1;
    return a.date.localeCompare(b.date);
  }) : [];

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        onClick={onClose}
      />
      {/* Drawer */}
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        className="fixed right-0 top-0 h-full w-full max-w-[520px] bg-card border-l border-border z-50 flex flex-col overflow-hidden"
        style={{ boxShadow: "-8px 0 40px rgba(0,0,0,0.2)" }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-border shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-bold text-foreground truncate">{data?.name || email}</h2>
              {data?.isClosed && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Closed Won
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{email}</p>
            {data && (data.phone || data.country) && (
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {[data.phone, data.country].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
          <button onClick={onClose}
            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center hover:bg-muted/60 text-muted-foreground transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* KPI strip */}
        {data && (
          <div className="flex gap-3 px-5 py-3 border-b border-border shrink-0">
            {[
              { label: "Optins", value: data.optins.length + (data.webinarOptins?.length ?? 0) },
              { label: "Applied", value: data.application ? "Yes" : "No" },
              { label: "Meetings", value: (data.meetingBookings?.length ?? 0) + data.calls.length },
              { label: "Revenue", value: data.totalRevenue > 0 ? `$${data.totalRevenue.toLocaleString()}` : "$0" },
            ].map(k => (
              <div key={k.label} className="flex-1 min-w-0 rounded-xl px-3 py-2 text-center"
                style={{ background: `rgba(${parseInt(pri.slice(1,3),16)},${parseInt(pri.slice(3,5),16)},${parseInt(pri.slice(5,7),16)},0.06)`, border: `1px solid rgba(${parseInt(pri.slice(1,3),16)},${parseInt(pri.slice(3,5),16)},${parseInt(pri.slice(5,7),16)},0.12)` }}>
                <div className="text-[10px] text-muted-foreground font-medium mb-0.5">{k.label}</div>
                <div className="text-[15px] font-bold text-foreground tabular-nums">{String(k.value)}</div>
              </div>
            ))}
          </div>
        )}

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-20 text-[11px] text-muted-foreground">
              <div className="w-1.5 h-1.5 rounded-full animate-pulse mr-2" style={{ background: pri }} />
              Loading timeline…
            </div>
          )}
          {!loading && events.length === 0 && (
            <div className="flex items-center justify-center py-20 text-[11px] text-muted-foreground">No events found for this lead.</div>
          )}
          {!loading && events.length > 0 && (
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-[19px] top-3 bottom-3 w-px bg-border" />

              <div className="space-y-5">
                {events.map((ev, i) => {
                  const isOptin   = ev.type === "optin";
                  const isApp     = ev.type === "app";
                  const isCall    = ev.type === "call";
                  const isWebinar = ev.type === "webinar";
                  const isMeeting = ev.type === "meeting";

                  const isClosed = isCall && new Set(["Closed","closed","Close","close","Won","won","Sale","sale"]).has((ev.data as TimelineCall).outcome);
                  const dotColor = isOptin ? sec : isApp ? pri : isWebinar ? "#f59e0b" : isMeeting ? "#8b5cf6" : isClosed ? "#10b981" : "var(--muted-foreground)";

                  const label = isOptin ? "Opted In" : isApp ? "Submitted Application" : isWebinar ? "Webinar Registration" : isMeeting ? "Meeting Booked" : "Call";

                  return (
                    <div key={i} className="flex gap-4 relative">
                      {/* Dot */}
                      <div className="shrink-0 w-10 flex flex-col items-center">
                        <div className="w-5 h-5 rounded-full border-2 border-background flex items-center justify-center z-10"
                          style={{ background: dotColor }}>
                          {isOptin && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2a5 5 0 110 10A5 5 0 0112 2zm0 12c-5.33 0-8 2.67-8 4v2h16v-2c0-1.33-2.67-4-8-4z"/></svg>}
                          {isApp && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
                          {isCall && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>}
                          {isWebinar && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M15 10l4.553-2.07A1 1 0 0121 8.845v6.31a1 1 0 01-1.447.894L15 14v-4zM3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/></svg>}
                          {isMeeting && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>}
                        </div>
                      </div>

                      {/* Card */}
                      <div className="flex-1 min-w-0 rounded-xl border border-border/60 bg-background/60 px-4 py-3 mb-1"
                        style={{ boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
                        {/* Event type + date */}
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <span className="text-[11px] font-semibold text-foreground">{label}</span>
                          <span className="text-[10px] text-muted-foreground shrink-0">{ev.date ? fmtDate(ev.date) : "—"}</span>
                        </div>

                        {/* Optin details */}
                        {isOptin && (() => {
                          const o = ev.data as TimelineOptin;
                          return (
                            <div className="space-y-0.5">
                              {o.source && <div className="text-[10px] text-muted-foreground">Source: <span className="text-foreground/80">{o.source}</span></div>}
                              {o.medium && <div className="text-[10px] text-muted-foreground">Medium: <span className="text-foreground/80">{o.medium}</span></div>}
                              {o.campaign && <div className="text-[10px] text-muted-foreground">Campaign: <span className="text-foreground/80 truncate">{o.campaign}</span></div>}
                              {o.content && <div className="text-[10px] text-muted-foreground">Content: <span className="text-foreground/80 truncate">{o.content}</span></div>}
                              {o.sheet && <div className="text-[10px] text-muted-foreground">Sheet: <span className="text-foreground/80">{o.sheet}</span></div>}
                            </div>
                          );
                        })()}

                        {/* Webinar registration details */}
                        {isWebinar && (() => {
                          const w = ev.data as TimelineWebinarOptin;
                          return (
                            <div className="space-y-0.5">
                              {w.webinarName && <div className="text-[10px] text-muted-foreground">Webinar: <span className="text-foreground/80">{w.webinarName}</span></div>}
                              {w.source && <div className="text-[10px] text-muted-foreground">Source: <span className="text-foreground/80">{w.source}</span></div>}
                              {w.campaign && <div className="text-[10px] text-muted-foreground">Campaign: <span className="text-foreground/80 truncate">{w.campaign}</span></div>}
                            </div>
                          );
                        })()}

                        {/* Meeting booking details */}
                        {isMeeting && (() => {
                          const m = ev.data as TimelineMeetingBooking;
                          return (
                            <div className="space-y-0.5">
                              {m.meetingAt && <div className="text-[10px] text-muted-foreground">Scheduled for: <span className="text-foreground/80">{fmtDate(m.meetingAt)}</span></div>}
                              {m.closer && <div className="text-[10px] text-muted-foreground">Closer: <span className="text-foreground/80">{m.closer}</span></div>}
                            </div>
                          );
                        })()}

                        {/* Application details */}
                        {isApp && (() => {
                          const a = ev.data as TimelineApplication;
                          return (
                            <div className="space-y-0.5">
                              {a.source && <div className="text-[10px] text-muted-foreground">Source: <span className="text-foreground/80">{a.source}</span></div>}
                              {a.medium && <div className="text-[10px] text-muted-foreground">Medium: <span className="text-foreground/80">{a.medium}</span></div>}
                              {a.campaign && <div className="text-[10px] text-muted-foreground">Campaign: <span className="text-foreground/80 truncate">{a.campaign}</span></div>}
                              {a.responseType && <div className="text-[10px] text-muted-foreground">Type: <span className="text-foreground/80">{a.responseType}</span></div>}
                            </div>
                          );
                        })()}

                        {/* Call details */}
                        {isCall && (() => {
                          const c = ev.data as TimelineCall;
                          return (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <OutcomeBadge outcome={c.outcome} />
                                {c.cash > 0 && (
                                  <span className="text-[10px] font-semibold text-emerald-400">${c.cash.toLocaleString()}</span>
                                )}
                              </div>
                              {c.offer && <div className="text-[10px] text-muted-foreground">Offer: <span className="text-foreground/80">{c.offer}</span></div>}
                              {c.closer && <div className="text-[10px] text-muted-foreground">Closer: <span className="text-foreground/80">{c.closer}</span></div>}
                              {c.setter && <div className="text-[10px] text-muted-foreground">Setter: <span className="text-foreground/80">{c.setter}</span></div>}
                              {c.funnel && <div className="text-[10px] text-muted-foreground">Funnel: <span className="text-foreground/80">{c.funnel}</span></div>}
                              {c.notes && <div className="text-[10px] text-muted-foreground mt-1 italic">"{c.notes}"</div>}
                              {c.fathomLink && (
                                <a href={c.fathomLink} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[10px] mt-1"
                                  style={{ color: pri }}>
                                  View recording ↗
                                </a>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function Leads() {
  const { theme } = useTheme();
  const chartColors = branding.colors.chart;
  const { dateRange, formatForApi } = useDateRange();
  const {
    sources,
    countries,
    funnels,
    leadTypes,
    contents,
    setSources,
    setCountries,
    setFunnels,
    setLeadTypes,
    setContents,
    clearAllFilters,
  } = useFilters();

  // Local sync state for leads-only sync
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(Date.now());

  const triggerLeadsSync = useCallback(async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      const response = await fetch("/api/analytics/sync/leads", { method: "POST" });
      const result = await response.json();
      if (result.success) {
        setLastSyncTime(Date.now());
      }
    } catch (err) {
      console.error("Leads sync failed:", err);
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing]);

  const [isLoading, setIsLoading] = useState(true);
  const hasInitiallyLoaded = useRef(false); // Track if we've loaded data at least once
  const [summary, setSummary] = useState<LeadsDataSummary | null>(null);

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Lead timeline drawer
  const [selectedLeadEmail, setSelectedLeadEmail] = useState<string | null>(null);

  // Server-side search (with debouncing)
  const [searchInput, setSearchInput] = useState(""); // What user types
  const [searchQuery, setSearchQuery] = useState(""); // What we send to API (debounced)

  // Response type filter (local to Leads page, not in global filter context)
  const [responseTypes, setResponseTypes] = useState<string[]>([]);
  const [closers, setClosers] = useState<string[]>([]);
  const [setters, setSetters] = useState<string[]>([]);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput);
        setCurrentPage(1); // Reset to page 1 when search changes
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchInput, searchQuery]);

  // Fetch data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { startDate, endDate } = formatForApi();
      const params = new URLSearchParams();

      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      // Multi-select filters (comma-separated)
      if (leadTypes.length > 0) params.set("type", leadTypes.join(","));
      if (sources.length > 0) params.set("source", sources.join(","));
      if (countries.length > 0) params.set("country", countries.join(","));
      if (funnels.length > 0) params.set("funnel", funnels.join(","));
      if (contents.length > 0) params.set("content", contents.join(","));
      if (responseTypes.length > 0) params.set("responseType", responseTypes.join(","));
      if (closers.length > 0) params.set("closer", closers.join(","));
      if (setters.length > 0) params.set("setter", setters.join(","));

      // Add server-side search
      if (searchQuery.trim()) params.set("search", searchQuery.trim());

      // Add pagination params
      params.set("page", currentPage.toString());
      params.set("pageSize", pageSize.toString());

      const response = await fetch(`/api/leads-data/summary?${params.toString()}`);
      const result = await response.json();

      if (result.success) {
        setSummary(result.data);
        setTotalCount(result.data.totalCount || 0);
        setTotalPages(result.data.totalPages || 1);
        hasInitiallyLoaded.current = true; // Mark that we've loaded data at least once
      }
    } catch (error) {
      console.error("Failed to fetch leads data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, formatForApi, leadTypes, sources, countries, funnels, contents, responseTypes, closers, setters, searchQuery, currentPage, pageSize, lastSyncTime]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Speed to Lead state
  interface SpeedToLeadData {
    count: number;
    totalLeads: number;
    contacted: number;
    avg: number | null;
    median: number | null;
    p75: number | null;
    within1h: number;
    within24h: number;
    distribution: Array<{ label: string; count: number; pct: number }>;
    lastSyncedAt: string | null;
  }
  const [speedToLead, setSpeedToLead] = useState<SpeedToLeadData | null>(null);
  const [speedToLeadLoading, setSpeedToLeadLoading] = useState(true);

  const fetchSpeedToLead = useCallback(async () => {
    setSpeedToLeadLoading(true);
    try {
      const { startDate, endDate } = formatForApi();
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const res = await fetch(`/api/speed-to-lead?${params}`);
      const result = await res.json();
      if (result.success) setSpeedToLead(result.data);
    } catch (e) {
      console.error("Speed to lead fetch failed:", e);
    } finally {
      setSpeedToLeadLoading(false);
    }
  }, [dateRange, formatForApi]);

  useEffect(() => { fetchSpeedToLead(); }, [fetchSpeedToLead]);

  // Format minutes as "Xh Ym" or "Xm" or "Xs"
  const formatMinutes = (minutes: number): string => {
    if (minutes < 1) return `${Math.round(minutes * 60)}s`;
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, leadTypes, sources, countries, funnels, contents, responseTypes, closers, setters]);

  // Auto-remove invalid filters when available options change
  useEffect(() => {
    if (!summary?.filters) return;
    const opts = summary.filters;

    // Skip if filterOptions haven't loaded yet
    if (!opts.sources?.length && !opts.funnels?.length && !opts.countries?.length) {
      return;
    }

    // Check source filter
    if (sources.length > 0 && opts.sources?.length > 0) {
      const validSources = sources.filter(s => opts.sources.some((opt: string | { value: string }) =>
        typeof opt === 'string' ? opt === s : opt.value === s
      ));
      if (validSources.length !== sources.length) {
        setSources(validSources);
      }
    }

    // Check funnel filter
    if (funnels.length > 0 && opts.funnels?.length > 0) {
      const validFunnels = funnels.filter(f => opts.funnels.some((opt: string | { value: string }) =>
        typeof opt === 'string' ? opt === f : opt.value === f
      ));
      if (validFunnels.length !== funnels.length) {
        setFunnels(validFunnels);
      }
    }

    // Check content filter
    if (contents.length > 0 && opts.contents?.length > 0) {
      const validContents = contents.filter(c => opts.contents.some((opt: string | { value: string }) =>
        typeof opt === 'string' ? opt === c : opt.value === c
      ));
      if (validContents.length !== contents.length) {
        setContents(validContents);
      }
    }

    // Check country filter
    if (countries.length > 0 && opts.countries?.length > 0) {
      const validCountries = countries.filter(c => opts.countries.some((opt: string | { value: string }) =>
        typeof opt === 'string' ? opt === c : opt.value === c
      ));
      if (validCountries.length !== countries.length) {
        setCountries(validCountries);
      }
    }

    // Check lead type filter
    if (leadTypes.length > 0) {
      const availableLeadTypes = summary.availableLeadTypes || [];
      const validLeadTypes = leadTypes.filter((t) => availableLeadTypes.includes(t));
      if (validLeadTypes.length !== leadTypes.length) {
        setLeadTypes(validLeadTypes);
      }
    }
  }, [summary?.filters, summary?.availableLeadTypes, sources, funnels, contents, countries, leadTypes, setSources, setFunnels, setContents, setCountries, setLeadTypes]);

  // Handle clear all filters
  const handleClearAll = () => {
    setCurrentPage(1);
    setSearchInput("");
    setSearchQuery("");
    setResponseTypes([]);
    setClosers([]);
    setSetters([]);
    clearAllFilters();
  };

  // Chart click handlers - toggle items in multi-select arrays
  const handleFunnelClick = (name: string) => {
    // Map display name to filter value
    const value = name === "Applicant" ? "applicant" : name === "Opt-in" ? "optin" : name === "DM Call Booking" ? "dm_booking" : name.toLowerCase();
    if (leadTypes.includes(value)) {
      setLeadTypes(leadTypes.filter((t) => t !== value));
    } else {
      setLeadTypes([...leadTypes, value]);
    }
  };

  const handleSourceClick = (name: string) => {
    if (name === "Other" || name === "N/A") return;
    if (sources.includes(name)) {
      setSources(sources.filter((s) => s !== name));
    } else {
      setSources([...sources, name]);
    }
  };

  const handleFunnelSourceClick = (name: string) => {
    if (name === "Other" || name === "N/A") return;
    const matchingOption = filterOptions.funnels.find((opt) => {
      const option = toFilterOption(opt);
      return option.label === name;
    });
    const value = matchingOption ? toFilterOption(matchingOption).value : name;
    if (funnels.includes(value)) {
      setFunnels(funnels.filter((f) => f !== value));
    } else {
      setFunnels([...funnels, value]);
    }
  };

  const handleCountryClick = (name: string) => {
    if (name === "Other") return;
    // Convert display name to country code for the filter
    const countryCode = countryNameToCode[name] || name;
    if (countries.includes(countryCode)) {
      setCountries(countries.filter((c) => c !== countryCode));
    } else {
      setCountries([...countries, countryCode]);
    }
  };

  const handleResponseTypeClick = (name: string) => {
    // Convert display name to lowercase for the filter ("Partial" -> "partial", "Full" -> "full")
    const value = name.toLowerCase();
    if (responseTypes.includes(value)) {
      setResponseTypes(responseTypes.filter((t) => t !== value));
    } else {
      setResponseTypes([...responseTypes, value]);
    }
  };

  // Table columns matching old dashboard
  const columns: ColumnDef<Lead>[] = useMemo(() => [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      className: "font-medium min-w-[120px]",
    },
    {
      id: "email",
      header: "Email",
      cell: (row) => (
        <span className="text-sm truncate max-w-[150px] block text-muted-foreground" title={row.email}>
          {row.email || "-"}
        </span>
      ),
    },
    {
      id: "phone",
      header: "Phone",
      cell: (row) => (
        <span className="text-sm font-mono truncate max-w-[120px] block text-muted-foreground">{row.phone || "-"}</span>
      ),
    },
    {
      id: "source",
      header: "Source",
      cell: (row) => (
        <span className="text-sm px-2 py-1 rounded-lg bg-muted/50">{row.utm_source || "N/A"}</span>
      ),
    },
    {
      id: "funnel",
      header: "Funnel",
      cell: (row) => (
        <span className="text-sm truncate max-w-[120px] block" title={row.utm_medium}>
          {row.utm_medium || "N/A"}
        </span>
      ),
    },
    {
      id: "campaign",
      header: "Campaign",
      cell: (row) => (
        <span className="text-sm truncate max-w-[100px] block text-muted-foreground" title={row.utm_campaign}>
          {row.utm_campaign || "N/A"}
        </span>
      ),
    },
    {
      id: "content",
      header: "Content",
      cell: (row) => (
        <span className="text-sm truncate max-w-[100px] block text-muted-foreground" title={row.utm_content}>
          {row.utm_content || "N/A"}
        </span>
      ),
    },
    {
      id: "type",
      header: "Type",
      cell: (row) => {
        const isApplicant = row.type === "applicant";
        return (
          <motion.span
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className={`
              inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold
              ${isApplicant
                ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20"
                : "bg-sky-500/10 text-sky-400 border border-sky-500/20"
              }
            `}
          >
            <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${isApplicant ? "bg-emerald-500" : "bg-sky-400"}`} />
            {isApplicant ? "Applicant" : "Opt-in"}
          </motion.span>
        );
      },
    },
    {
      id: "submissionType",
      header: "Submission",
      cell: (row) => {
        if (row.type !== "applicant" || !row.responseType) return <span className="text-xs text-muted-foreground/40">—</span>;
        const isFull = row.responseType.toLowerCase().startsWith("full");
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border ${
            isFull
              ? "bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border-[var(--brand-primary)]/20"
              : "bg-amber-500/10 text-amber-400 border-amber-500/20"
          }`}>
            {isFull ? "Full" : "Partial"}
          </span>
        );
      },
    },
    {
      id: "date",
      header: "Date",
      cell: (row) => (
        <span className="text-sm text-muted-foreground">
          {row.createdAt ? format(new Date(row.createdAt), "MMM d, yyyy") : "-"}
        </span>
      ),
      className: "whitespace-nowrap",
    },
  ], []);

  // Build filter options with safe mapping to ensure label is always a string
  const toFilterOption = (item: string | ContentOption): { value: string; label: string } => {
    if (typeof item === 'string') {
      return { value: item, label: item };
    }
    // Ensure label is always a string
    return {
      value: String(item.value ?? ''),
      label: String(item.label ?? item.value ?? ''),
    };
  };

  const filterOptions = summary?.filters || {
    sources: [] as (string | ContentOption)[],
    funnels: [] as (string | ContentOption)[],
    contents: [] as (string | ContentOption)[],
    campaigns: [] as string[],
    countries: [] as (string | ContentOption)[],
    closers: [] as string[],
    setters: [] as string[],
  };

  const leadTypeFilterOptions = useMemo(() => {
    const available = summary?.availableLeadTypes || ["applicant", "optin"];
    return [
      available.includes("applicant") ? { value: "applicant", label: "Applicant" } : null,
      available.includes("optin") ? { value: "optin", label: "Opt-in" } : null,
      available.includes("dm_booking") ? { value: "dm_booking", label: "DM Call Booking" } : null,
    ].filter((option): option is { value: string; label: string } => option !== null);
  }, [summary?.availableLeadTypes]);

  // Create funnel breakdown data (Applicant vs Opt-in)
  const funnelBreakdownData = useMemo(() => {
    if (!summary?.totals) return [];
    return [
      { name: "Applicant", value: summary.totals.applicants },
      { name: "Opt-in", value: summary.totals.optins },
      { name: "DM Call Booking", value: summary.totals.dmBookings || 0 },
    ].filter(item => item.value > 0);
  }, [summary?.totals]);

  // Create source breakdown data with "N/A" for missing sources
  const sourceBreakdownData = useMemo(() => {
    if (!summary?.sourceBreakdown) return [];
    return summary.sourceBreakdown.map(item => ({
      name: item.name || "N/A",
      value: item.value,
    }));
  }, [summary?.sourceBreakdown]);

  const funnelSourceBreakdownData = useMemo(() => {
    if (!summary?.funnelSourceBreakdown) return [];
    return summary.funnelSourceBreakdown.map((item) => ({
      name: item.name || "N/A",
      value: item.value,
    }));
  }, [summary?.funnelSourceBreakdown]);

  const applicantIncomeBreakdownData = useMemo(() => {
    if (!summary?.applicantIncomeBreakdown) return [];
    return summary.applicantIncomeBreakdown;
  }, [summary?.applicantIncomeBreakdown]);

  const investmentCloseRates = useMemo(() => {
    if (!summary?.investmentCloseRates) return [];
    return summary.investmentCloseRates;
  }, [summary?.investmentCloseRates]);

  const responseTypeBreakdownData = useMemo(() => {
    if (!summary?.responseTypeBreakdown) return [];
    return summary.responseTypeBreakdown;
  }, [summary?.responseTypeBreakdown]);

  // Leads are now filtered server-side, just return them directly
  const filteredLeads = useMemo(() => {
    return summary?.leads || [];
  }, [summary?.leads]);

  // Determine if "All Time" is selected (year 2000 or earlier means all time)
  const isAllTime = dateRange.startDate.getFullYear() <= 2000;

  // Chart data for Recharts - aggregate by month for All Time, daily for custom range
  const chartData = useMemo(() => {
    if (!summary?.dailyTrend) return [];

    if (isAllTime) {
      // Aggregate by month for All Time view
      const monthlyData = new Map<string, number>();

      summary.dailyTrend.forEach((d) => {
        const date = new Date(d._id);
        const monthKey = format(date, "yyyy-MM");
        const current = monthlyData.get(monthKey) || 0;
        monthlyData.set(monthKey, current + d.total);
      });

      // Convert to array and sort by date
      return Array.from(monthlyData.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([monthKey, total]) => ({
          date: format(new Date(monthKey + "-01"), "MMM yyyy"),
          total,
        }));
    } else {
      // Show daily data for custom date ranges
      return summary.dailyTrend.map((d) => ({
        date: format(new Date(d._id), "MMM d"),
        total: d.total,
      }));
    }
  }, [summary?.dailyTrend, isAllTime]);

  // Only show full skeleton on initial load, not when filtering
  if (isLoading && !hasInitiallyLoaded.current) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="h-10 w-48 rounded-xl bg-muted animate-pulse mx-auto" />
        </motion.div>

        {/* Filter Bar Skeleton */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="h-16 rounded-xl bg-muted/30 animate-pulse"
        />

        {/* Summary Cards Skeleton */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.1 }}
              className="relative overflow-hidden bg-card rounded-2xl border border-border p-6"
            >
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-muted animate-pulse" />
                  <div className="h-4 w-24 rounded bg-muted animate-pulse" />
                </div>
                <div className="h-10 w-32 rounded-lg bg-muted animate-pulse" />
              </div>
              <motion.div
                className="absolute inset-0"
                animate={{ x: ["-100%", "100%"] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                style={{
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)",
                }}
              />
            </motion.div>
          ))}
        </div>

        {/* Chart Skeletons */}
        <div className="grid gap-6 md:grid-cols-2">
          {[0, 1].map((i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.1 }}
              className="relative overflow-hidden bg-card rounded-2xl border border-border p-6"
            >
              <div className="h-5 w-40 rounded bg-muted animate-pulse mb-4" />
              <div className="h-[400px] rounded-xl bg-muted/30 animate-pulse flex items-center justify-center">
                <motion.div
                  className="w-32 h-32 rounded-full border-4 border-muted border-t-muted-foreground/30"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                />
              </div>
              <motion.div
                className="absolute inset-0"
                animate={{ x: ["-100%", "100%"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                style={{
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)",
                }}
              />
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  const totals = summary?.totals || { total: 0, applicants: 0, optins: 0, dmBookings: 0 };

  return (
    <div className="space-y-8 w-full max-w-full overflow-x-hidden">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0"
      >
        <div className="hidden sm:block flex-1" /> {/* Spacer for centering */}
        <div className="text-center">
          <motion.h1
            className="text-2xl md:text-4xl font-bold tracking-tight"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            <span className="bg-linear-to-r from-foreground via-foreground/80 to-foreground/60 bg-clip-text text-transparent">
              LEADS DATA
            </span>
          </motion.h1>
          <motion.p
            className="text-sm md:text-base text-muted-foreground mt-1 md:mt-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Track and analyze your lead generation performance
          </motion.p>
        </div>
        <div className="sm:flex-1 flex justify-end">
          <motion.button
            onClick={triggerLeadsSync}
            disabled={isSyncing}
            className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-lg bg-card border border-border hover:bg-secondary/50 transition-colors disabled:opacity-50 text-sm md:text-base"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <svg
              className={`w-4 h-4 ${isSyncing ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span className="text-sm font-medium">
              {isSyncing ? "Syncing..." : "Sync Leads"}
            </span>
          </motion.button>
        </div>
      </motion.div>

      {/* Filter Bar */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        <FilterBar
          filters={[
            {
              key: "source",
              label: "Select Source",
              options: filterOptions.sources.map(toFilterOption),
              value: sources,
              onChange: (v) => setSources(v as string[]),
              multiple: true,
            },
            {
              key: "funnel",
              label: "Select Funnel",
              options: filterOptions.funnels.map(toFilterOption),
              value: funnels,
              onChange: (v) => setFunnels(v as string[]),
              multiple: true,
            },
            {
              key: "content",
              label: "Select UTM Content",
              options: filterOptions.contents.map(toFilterOption),
              value: contents,
              onChange: (v) => setContents(v as string[]),
              multiple: true,
            },
            {
              key: "type",
              label: "Lead Type",
              options: leadTypeFilterOptions,
              value: leadTypes,
              onChange: (v) => setLeadTypes(v as string[]),
              multiple: true,
            },
            {
              key: "responseType",
              label: "Response Type",
              options: [
                { value: "partial", label: "Partial" },
                { value: "full", label: "Full" },
              ],
              value: responseTypes,
              onChange: (v) => setResponseTypes(v as string[]),
              multiple: true,
            },
            {
              key: "country",
              label: "Select Country",
              options: filterOptions.countries.map(toFilterOption),
              value: countries,
              onChange: (v) => setCountries(v as string[]),
              multiple: true,
            },
            {
              key: "closer",
              label: "Select Closers",
              options: (filterOptions.closers || []).map(v => ({ value: v, label: v })),
              value: closers,
              onChange: (v) => setClosers(v as string[]),
              multiple: true,
            },
            {
              key: "setter",
              label: "Select Setters",
              options: (filterOptions.setters || []).map(v => ({ value: v, label: v })),
              value: setters,
              onChange: (v) => setSetters(v as string[]),
              multiple: true,
            },
          ]}
          onClearAll={handleClearAll}
        />
      </motion.div>

      {/* Leads Summary Cards */}
      <LeadsSummaryCards totals={totals} previousTotals={summary?.previousTotals ?? null} isLoading={isLoading} />

      {/* Lead Type & Lead Source Breakdown */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Lead Type Breakdown (Applicant vs Opt-in) */}
        <ChartCard
          title="LEAD TYPE"
          delay={0.4}
          activeFilters={leadTypes.map(t => t === "applicant" ? "Applicant" : t === "optin" ? "Opt-in" : "DM Call Booking")}
          onClearFilters={() => setLeadTypes([])}
        >
          {funnelBreakdownData.length > 0 ? (
            <ResponsivePie
              data={toNivoPieData(funnelBreakdownData, branding.colors.chart)}
              margin={{ top: 30, right: 120, bottom: 30, left: 120 }}
              innerRadius={0.55}
              padAngle={2}
              cornerRadius={6}
              activeOuterRadiusOffset={10}
              sortByValue={false}
              colors={{ datum: "data.color" }}
              borderWidth={0}
              enableArcLinkLabels={true}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLinkLabel={(d) => String(d.label)}
              arcLinkLabelsStraightLength={12}
              arcLinkLabelsDiagonalLength={12}
              enableArcLabels={true}
              arcLabelsSkipAngle={10}
              arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
              arcLabelsTextColor="#ffffff"
              theme={getNivoPieTheme()}
              tooltip={PieChartTooltip}
              onClick={(node) => handleFunnelClick(String(node.label))}
              animate
              motionConfig="gentle"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                  <UsersIcon />
                </div>
                No lead type data available for the selected filters.
              </motion.div>
            </div>
          )}
        </ChartCard>

        {/* Lead Source Breakdown */}
        <ChartCard
          title="LEAD SOURCE BREAKDOWN"
          delay={0.5}
          activeFilters={sources}
          onClearFilters={() => setSources([])}
        >
          {sourceBreakdownData.length > 0 ? (
            <ResponsivePie
              data={toNivoPieData(sourceBreakdownData, chartColors)}
              margin={{ top: 30, right: 120, bottom: 30, left: 120 }}
              innerRadius={0.55}
              padAngle={2}
              cornerRadius={6}
              activeOuterRadiusOffset={10}
              sortByValue={false}
              colors={{ datum: "data.color" }}
              borderWidth={0}
              enableArcLinkLabels={true}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLinkLabel={(d) => `${truncateLabel(String(d.label))} (${d.data.percentage.toFixed(1)}%)`}
              arcLinkLabelsStraightLength={12}
              arcLinkLabelsDiagonalLength={12}
              enableArcLabels={false}
              theme={getNivoPieTheme()}
              tooltip={PieChartTooltip}
              onClick={(node) => handleSourceClick(String(node.label))}
              animate
              motionConfig="gentle"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                  <SearchIcon />
                </div>
                No source data available for the selected filters.
              </motion.div>
            </div>
          )}
        </ChartCard>
      </div>

      {/* Lead Type × Submission Type breakdown — applicants only */}
      {(leadTypes.length === 0 || leadTypes.includes("applicant")) && responseTypeBreakdownData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="rounded-2xl border border-border bg-card p-6"
        >
          <h3 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-4">
            Lead Type · Submission Breakdown
          </h3>
          <div className="space-y-3">
            {(() => {
              const fullItem = responseTypeBreakdownData.find(d => d.name === "Full");
              const partialItem = responseTypeBreakdownData.find(d => d.name === "Partial");
              const full = fullItem?.value ?? 0;
              const partial = partialItem?.value ?? 0;
              const total = full + partial;
              const fullPct = total > 0 ? Math.round((full / total) * 100) : 0;
              const partialPct = total > 0 ? Math.round((partial / total) * 100) : 0;
              return (
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-foreground">Applicant</span>
                    <span className="text-muted-foreground tabular-nums">{total.toLocaleString()} applicants</span>
                  </div>
                  <div className="flex h-5 w-full rounded-md overflow-hidden gap-px">
                    {full > 0 && (
                      <div
                        className="flex items-center justify-center text-[10px] font-semibold text-white bg-[var(--brand-primary)] transition-all"
                        style={{ width: `${fullPct}%` }}
                        title={`Full: ${full} (${fullPct}%)`}
                      >
                        {fullPct >= 12 ? `${fullPct}%` : ""}
                      </div>
                    )}
                    {partial > 0 && (
                      <div
                        className="flex items-center justify-center text-[10px] font-semibold text-white bg-amber-500 transition-all"
                        style={{ width: `${partialPct}%` }}
                        title={`Partial: ${partial} (${partialPct}%)`}
                      >
                        {partialPct >= 12 ? `${partialPct}%` : ""}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[var(--brand-primary)] inline-block" />Full: {full.toLocaleString()}</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500 inline-block" />Partial: {partial.toLocaleString()}</span>
                  </div>
                </div>
              );
            })()}
            {(leadTypes.length === 0 || leadTypes.includes("optin")) && (summary?.totals?.optins ?? 0) > 0 && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-foreground">Opt-in</span>
                  <span className="text-muted-foreground tabular-nums">{(summary?.totals?.optins ?? 0).toLocaleString()} opt-ins</span>
                </div>
                <div className="h-5 w-full rounded-md bg-[var(--brand-secondary)]/30 flex items-center px-2">
                  <span className="text-[10px] text-muted-foreground">No submission type (opt-in only)</span>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 mt-4 text-[10px] text-muted-foreground border-t border-border/40 pt-3">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[var(--brand-primary)] inline-block" />Full = completed application</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500 inline-block" />Partial = form not fully completed</span>
          </div>
        </motion.div>
      )}

      {/* Lead Filters: Source / Funnel / UTM Content */}
      {/* Response Type + Funnel Source */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Response Type Breakdown (Partial vs Full) - Only for Applicants */}
        {leadTypes.length === 0 || leadTypes.includes("applicant") ? (
          <ChartCard
            title="SUBMISSION TYPE (APPLICANTS ONLY)"
            delay={0.55}
            activeFilters={responseTypes.map(t => t.charAt(0).toUpperCase() + t.slice(1))}
            onClearFilters={() => setResponseTypes([])}
          >
            {responseTypeBreakdownData.length > 0 ? (
            <ResponsivePie
              data={toNivoPieData(responseTypeBreakdownData, [branding.colors.chart[0], branding.colors.chart[2]])}
              margin={{ top: 30, right: 120, bottom: 30, left: 120 }}
              innerRadius={0.55}
              padAngle={2}
              cornerRadius={6}
              activeOuterRadiusOffset={10}
              sortByValue={false}
              colors={{ datum: "data.color" }}
              borderWidth={0}
              enableArcLinkLabels={true}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLinkLabel={(d) => String(d.label)}
              arcLinkLabelsStraightLength={12}
              arcLinkLabelsDiagonalLength={12}
              enableArcLabels={true}
              arcLabelsSkipAngle={10}
              arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
              arcLabelsTextColor="#ffffff"
              theme={getNivoPieTheme()}
              tooltip={PieChartTooltip}
              onClick={(node) => handleResponseTypeClick(String(node.label))}
              animate
              motionConfig="gentle"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                No submission type data available for the selected filters.
              </motion.div>
            </div>
          )}
        </ChartCard>
        ) : null}

        <ChartCard
          title="FUNNEL SOURCE BREAKDOWN"
          delay={0.6}
          activeFilters={funnels.map((value) => {
            const matching = filterOptions.funnels.find((opt) => toFilterOption(opt).value === value);
            return matching ? toFilterOption(matching).label : value;
          })}
          onClearFilters={() => setFunnels([])}
        >
          {funnelSourceBreakdownData.length > 0 ? (
            <ResponsivePie
              data={toNivoPieData(funnelSourceBreakdownData, chartColors)}
              margin={{ top: 30, right: 120, bottom: 30, left: 120 }}
              innerRadius={0.55}
              padAngle={2}
              cornerRadius={6}
              activeOuterRadiusOffset={10}
              sortByValue={false}
              colors={{ datum: "data.color" }}
              borderWidth={0}
              enableArcLinkLabels={true}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLinkLabel={(d) => `${truncateLabel(String(d.label))} (${d.data.percentage.toFixed(1)}%)`}
              arcLinkLabelsStraightLength={12}
              arcLinkLabelsDiagonalLength={12}
              enableArcLabels={false}
              theme={getNivoPieTheme()}
              tooltip={PieChartTooltip}
              onClick={(node) => handleFunnelSourceClick(String(node.label))}
              animate
              motionConfig="gentle"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                  <SearchIcon />
                </div>
                No funnel source data available for the selected filters.
              </motion.div>
            </div>
          )}
        </ChartCard>

      </div>

      {/* Funnel × Submission Type breakdown — applicants only */}
      {(leadTypes.length === 0 || leadTypes.includes("applicant")) && (summary?.funnelSubmissionBreakdown ?? []).length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="rounded-2xl border border-border bg-card p-6"
        >
          <h3 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-4">
            Funnel Source · Submission Type Breakdown
          </h3>
          <div className="space-y-3">
            {(summary?.funnelSubmissionBreakdown ?? []).map((row) => {
              const fullPct  = row.total > 0 ? Math.round((row.full  / row.total) * 100) : 0;
              const partialPct = row.total > 0 ? Math.round((row.partial / row.total) * 100) : 0;
              return (
                <div key={row.funnel} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-foreground truncate max-w-[55%]" title={row.funnel}>{row.funnel}</span>
                    <span className="text-muted-foreground tabular-nums">{row.total.toLocaleString()} applicants</span>
                  </div>
                  <div className="flex h-5 w-full rounded-md overflow-hidden gap-px">
                    {row.full > 0 && (
                      <div
                        className="flex items-center justify-center text-[10px] font-semibold text-white bg-[var(--brand-primary)] transition-all"
                        style={{ width: `${fullPct}%` }}
                        title={`Full: ${row.full} (${fullPct}%)`}
                      >
                        {fullPct >= 12 ? `${fullPct}%` : ""}
                      </div>
                    )}
                    {row.partial > 0 && (
                      <div
                        className="flex items-center justify-center text-[10px] font-semibold text-white bg-amber-500 transition-all"
                        style={{ width: `${partialPct}%` }}
                        title={`Partial: ${row.partial} (${partialPct}%)`}
                      >
                        {partialPct >= 12 ? `${partialPct}%` : ""}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[var(--brand-primary)] inline-block" />Full: {row.full}</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500 inline-block" />Partial: {row.partial}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Lead Conversion Rate Cards */}
      <div className="grid gap-6 grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="relative overflow-hidden border rounded-2xl p-6 bg-card"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1">Leads → Call Rate</p>
              <div className="flex items-end gap-3 mt-2">
                <span className="text-5xl font-bold text-foreground leading-none">
                  {summary?.overallCallRate ?? 0}%
                </span>
                <span className="text-sm text-muted-foreground mb-1">
                  {(summary?.overallCallCount ?? 0).toLocaleString()} of {((summary?.totals?.applicants ?? 0) + (summary?.totals?.optins ?? 0) + (summary?.totals?.dmBookings ?? 0)).toLocaleString()} leads got on a call
                </span>
              </div>
              <div className="mt-4 h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-[var(--brand-primary)] transition-all duration-700"
                  style={{ width: `${Math.min(summary?.overallCallRate ?? 0, 100)}%` }}
                />
              </div>
            </div>
            {(summary?.callRateTrend ?? []).length > 0 && (
              <div className="w-32 h-16 shrink-0 overflow-hidden">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={summary?.callRateTrend ?? []} margin={{ top: 5, right: 2, left: 2, bottom: 5 }}>
                    <defs>
                      <linearGradient id="callTrendGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={branding.colors.chart[0]} stopOpacity={0.4} />
                        <stop offset="100%" stopColor={branding.colors.chart[0]} stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="value" stroke={branding.colors.chart[0]} fill="url(#callTrendGrad)" strokeWidth={2} dot={false} animationDuration={1200} />
                    <Tooltip
                      cursor={{ stroke: branding.colors.chart[0], strokeWidth: 1, strokeDasharray: "4 4" }}
                      content={({ active, payload }) => {
                        if (active && payload?.length) {
                          return (
                            <div className="bg-background/95 border border-border/50 rounded-lg p-2 text-xs font-semibold text-foreground shadow-xl">
                              {(payload[0].value as number).toLocaleString()} calls
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
          className="relative overflow-hidden border rounded-2xl p-6 bg-card"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1">Leads → Close Rate</p>
              <div className="flex items-end gap-3 mt-2">
                <span className="text-5xl font-bold text-foreground leading-none">
                  {summary?.overallCloseRate ?? 0}%
                </span>
                <span className="text-sm text-muted-foreground mb-1">
                  {(summary?.overallCloseCount ?? 0).toLocaleString()} of {((summary?.totals?.applicants ?? 0) + (summary?.totals?.optins ?? 0) + (summary?.totals?.dmBookings ?? 0)).toLocaleString()} leads closed
                </span>
              </div>
              <div className="mt-4 h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-[var(--brand-primary)] transition-all duration-700"
                  style={{ width: `${Math.min(summary?.overallCloseRate ?? 0, 100)}%` }}
                />
              </div>
            </div>
            {(summary?.closeRateTrend ?? []).length > 0 && (
              <div className="w-32 h-16 shrink-0 overflow-hidden">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={summary?.closeRateTrend ?? []} margin={{ top: 5, right: 2, left: 2, bottom: 5 }}>
                    <defs>
                      <linearGradient id="closeTrendGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={branding.colors.chart[1]} stopOpacity={0.4} />
                        <stop offset="100%" stopColor={branding.colors.chart[1]} stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="value" stroke={branding.colors.chart[1]} fill="url(#closeTrendGrad)" strokeWidth={2} dot={false} animationDuration={1200} />
                    <Tooltip
                      cursor={{ stroke: branding.colors.chart[1], strokeWidth: 1, strokeDasharray: "4 4" }}
                      content={({ active, payload }) => {
                        if (active && payload?.length) {
                          return (
                            <div className="bg-background/95 border border-border/50 rounded-lg p-2 text-xs font-semibold text-foreground shadow-xl">
                              {(payload[0].value as number).toLocaleString()} closes
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Speed to Lead */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="relative overflow-hidden border rounded-2xl p-6 bg-card"
      >
        <div className="mb-4">
          <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">Speed to Lead</p>
          <p className="text-xs text-muted-foreground/60 mt-0.5">Time from lead entering CRM (01.1) to first SDR outreach (01.2)</p>
        </div>

        {speedToLeadLoading ? (
          <div className="space-y-3">
            <div className="h-12 w-48 bg-muted/40 animate-pulse rounded-lg" />
            <div className="h-4 w-64 bg-muted/30 animate-pulse rounded" />
            <div className="space-y-2 mt-4">
              {[0,1,2,3,4,5].map(i => <div key={i} className="h-3 bg-muted/25 animate-pulse rounded" />)}
            </div>
          </div>
        ) : !speedToLead || speedToLead.count === 0 ? (
          <p className="text-sm text-muted-foreground">No matched speed-to-lead data for this period.</p>
        ) : (
          <>
            {/* Big number + sub stats */}
            <div className="flex flex-wrap items-end gap-x-8 gap-y-3 mb-5">
              <div>
                <span className="text-5xl font-bold text-foreground leading-none">
                  {formatMinutes(speedToLead.avg ?? 0)}
                </span>
                <span className="text-sm text-muted-foreground ml-2">avg</span>
              </div>
              <div className="flex gap-6">
                <div>
                  <p className="text-xs text-muted-foreground">Contacted</p>
                  <p className="text-sm font-bold">
                    {speedToLead.count.toLocaleString()}
                    {speedToLead.totalLeads > 0 && (
                      <span className="text-muted-foreground font-normal"> / {speedToLead.totalLeads.toLocaleString()} ({Math.round(speedToLead.count / speedToLead.totalLeads * 100)}%)</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Median</p>
                  <p className="text-sm font-bold">{formatMinutes(speedToLead.median ?? 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">75th pct</p>
                  <p className="text-sm font-bold">{formatMinutes(speedToLead.p75 ?? 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Within 1hr</p>
                  <p className="text-sm font-bold">{speedToLead.within1h.toFixed(0)}%</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Within 24hr</p>
                  <p className="text-sm font-bold">{speedToLead.within24h.toFixed(0)}%</p>
                </div>
              </div>
            </div>
            {speedToLead.lastSyncedAt && (
              <p className="text-[10px] text-muted-foreground/50 mb-3">
                CRM data synced: {new Date(speedToLead.lastSyncedAt).toLocaleString()}
              </p>
            )}

            {/* Distribution bars */}
            <div className="space-y-2.5">
              {speedToLead.distribution.map((bucket) => (
                <div key={bucket.label} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-20 shrink-0">{bucket.label}</span>
                  <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[var(--brand-primary)] transition-all duration-700"
                      style={{ width: `${Math.max(Math.min(bucket.pct, 100), bucket.count > 0 ? 2 : 0)}%` }}
                    />
                  </div>
                  <span className="text-xs font-semibold text-foreground w-14 text-right tabular-nums">
                    {bucket.count} ({bucket.pct.toFixed(0)}%)
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </motion.div>

      {/* Applicant Monthly Income - Full Width */}
      <ChartCard
        title="APPLICANT INVESTMENT ABILITY"
        delay={0.65}
        height="h-[420px]"
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={applicantIncomeBreakdownData}
            margin={{ top: 32, right: 24, bottom: 16, left: 8 }}
            barCategoryGap="28%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="currentColor"
              strokeOpacity={0.08}
            />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 13, fill: "currentColor", opacity: 0.65, fontWeight: 500 }}
              tickMargin={10}
            />
            <YAxis
              allowDecimals={false}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: "currentColor", opacity: 0.55 }}
              width={36}
            />
            <Tooltip
              cursor={{ fill: "currentColor", fillOpacity: 0.06, radius: 8 }}
              contentStyle={{
                backgroundColor: "hsl(var(--popover))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "12px",
                color: "hsl(var(--popover-foreground))",
                boxShadow: "0 10px 30px -10px rgba(0,0,0,0.2)",
                padding: "10px 14px",
              }}
              itemStyle={{ color: "hsl(var(--popover-foreground))", fontWeight: 600 }}
              labelStyle={{ color: "hsl(var(--muted-foreground))", marginBottom: 4 }}
              formatter={(value) => [`${Number(value || 0).toLocaleString()} applicants`, ""]}
            />
            <Bar dataKey="value" radius={[10, 10, 0, 0]}>
              {applicantIncomeBreakdownData.map((_, index) => (
                <Cell
                  key={`income-cell-${index}`}
                  fill={branding.colors.chart[index % branding.colors.chart.length]}
                />
              ))}
              <LabelList
                dataKey="value"
                position="top"
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "currentColor",
                  opacity: 0.8,
                }}
                formatter={(v: number) => v > 0 ? v : ""}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Investment Close Rate Gauges */}
      {investmentCloseRates.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="rounded-2xl border bg-card/50 p-5 space-y-6"
        >
          {/* Row 1: % of all leads that closed */}
          <div>
            <div className="mb-3">
              <h3 className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                LEADS CLOSED BY INVESTMENT BUCKET
              </h3>
              <p className="text-xs text-muted-foreground/60 mt-0.5">
                % of all leads in each bucket that closed (email-matched to EOC)
              </p>
            </div>
            <div className="grid grid-cols-5 gap-4">
              {investmentCloseRates.map((item) => (
                <InvestmentCloseRateGauge
                  key={item.bucket}
                  bucket={item.bucket}
                  total={item.total}
                  closed={item.closed}
                  rate={item.rate}
                  sublabel="closed"
                />
              ))}
            </div>
          </div>

          <div className="border-t border-border/40" />

          {/* Row 2: % of leads who booked a call that closed */}
          <div>
            <div className="mb-3">
              <h3 className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                CALL CLOSE RATE BY INVESTMENT BUCKET
              </h3>
              <p className="text-xs text-muted-foreground/60 mt-0.5">
                % of leads who booked a call (in EOC) that actually closed
              </p>
            </div>
            <div className="grid grid-cols-5 gap-4">
              {investmentCloseRates.map((item) => (
                <InvestmentCloseRateGauge
                  key={item.bucket}
                  bucket={item.bucket}
                  total={item.calledTotal}
                  closed={item.closed}
                  rate={item.callCloseRate}
                  sublabel="call close"
                  statLabel="booked calls"
                  statValue={item.calledTotal}
                  totalLabel="calls"
                />
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Country Breakdown Chart */}
      <ChartCard
        title="COUNTRY BREAKDOWN"
        delay={0.6}
        activeFilters={countries.map(c => countryCodeToName[c] || c)}
        onClearFilters={() => setCountries([])}
      >
        {(summary?.countryBreakdown || []).length > 0 ? (
          <ResponsivePie
            data={toNivoPieData(summary?.countryBreakdown || [], chartColors)}
            margin={{ top: 30, right: 120, bottom: 30, left: 120 }}
            innerRadius={0.55}
            padAngle={2}
            cornerRadius={6}
            activeOuterRadiusOffset={10}
            sortByValue={false}
            colors={{ datum: "data.color" }}
            borderWidth={0}
            enableArcLinkLabels={true}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: "color" }}
            arcLinkLabel={(d) => `${truncateLabel(String(d.label))} (${d.data.percentage.toFixed(1)}%)`}
            arcLinkLabelsStraightLength={12}
            arcLinkLabelsDiagonalLength={12}
            enableArcLabels={false}
            theme={getNivoPieTheme()}
            tooltip={PieChartTooltip}
            onClick={(node) => handleCountryClick(String(node.label))}
            animate
            motionConfig="gentle"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              No country data available for the selected filters.
            </motion.div>
          </div>
        )}
      </ChartCard>

      {/* Total Leads Over Time Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
        whileHover={{
          y: -2,
          boxShadow: "0 20px 60px -15px rgba(0, 0, 0, 0.15)",
          transition: { duration: 0.3 },
        }}
        className="bg-card rounded-2xl border border-border p-6 overflow-hidden transition-all duration-300 hover:border-border/60"
      >
        <motion.h3
          className="text-lg font-bold mb-4 tracking-tight flex items-center gap-2"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
        >
          TOTAL LEADS OVER TIME
          <span className="text-xs font-normal text-muted-foreground px-2 py-0.5 rounded-full bg-muted">
            {isAllTime ? "Monthly" : "Daily"}
          </span>
        </motion.h3>
        <div className="h-[350px]">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 20, right: 20, bottom: 20, left: 10 }}
              >
                <defs>
                  <linearGradient id="leadsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity={0.4} />
                    <stop offset="50%" stopColor="#ef4444" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                    <feMerge>
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="currentColor"
                  strokeOpacity={0.1}
                />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "currentColor", opacity: 0.5 }}
                  dy={10}
                  interval={Math.max(0, Math.floor(chartData.length / 8) - 1)}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "currentColor", opacity: 0.5 }}
                  tickFormatter={(value) => value.toLocaleString()}
                  width={45}
                />
                <Tooltip content={<CustomChartTooltip />} cursor={{ stroke: '#ef4444', strokeWidth: 1, strokeDasharray: '4 4', strokeOpacity: 0.5 }} />
                <Area
                  type="monotone"
                  dataKey="total"
                  fill="url(#leadsGradient)"
                  stroke="none"
                  animationDuration={1500}
                  animationEasing="ease-out"
                />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#ef4444"
                  strokeWidth={3}
                  dot={false}
                  filter="url(#glow)"
                  activeDot={{
                    fill: "#ef4444",
                    strokeWidth: 3,
                    stroke: "#ffffff",
                    r: 6,
                    filter: "url(#glow)"
                  }}
                  animationDuration={1500}
                  animationEasing="ease-out"
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                  </svg>
                </div>
                No trend data available for the selected filters.
              </motion.div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Leads Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4">
          <div>
            <motion.h3
              className="text-xl md:text-2xl font-bold tracking-tight"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9 }}
            >
              All Leads
            </motion.h3>
            <motion.p
              className="text-xs md:text-sm text-muted-foreground mt-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.95 }}
            >
              Browse and search through your lead database
            </motion.p>
          </div>

          {/* Search and Filter Controls */}
          <motion.div
            className="flex items-center gap-2 md:gap-3 w-full sm:w-auto"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.9 }}
          >
            {/* Search Input */}
            <div className="relative group flex-1 sm:flex-none">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-muted-foreground group-focus-within:text-foreground transition-colors">
                <SearchIcon />
              </div>
              <input
                type="text"
                placeholder="Search by name, email, phone..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="h-9 md:h-10 w-full sm:w-[280px] rounded-xl border border-border bg-background pl-9 md:pl-10 pr-4 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring placeholder:text-muted-foreground hover:border-border/80"
              />
              {/* Loading indicator when search is pending */}
              {searchInput !== searchQuery && (
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                  <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                </div>
              )}
            </div>

            {/* Type Filter Dropdown - triggers server-side filtering */}
            <select
              value={leadTypes.length === 0 ? "all" : leadTypes.length === 1 ? leadTypes[0] : "all"}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "all") {
                  setLeadTypes([]);
                } else {
                  setLeadTypes([value]);
                }
                setCurrentPage(1);
              }}
              className="h-10 rounded-xl border border-border bg-background px-4 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring hover:border-border/80 cursor-pointer"
            >
              <option value="all">All Types</option>
              <option value="applicant">Applicants Only</option>
              <option value="optin">Opt-ins Only</option>
              <option value="dm_booking">DM Bookings Only</option>
            </select>
          </motion.div>
        </div>

        <motion.div
          whileHover={{
            boxShadow: "0 20px 60px -15px rgba(0, 0, 0, 0.1)",
            transition: { duration: 0.3 },
          }}
          className="rounded-2xl overflow-hidden"
        >
          <DataTable
            data={filteredLeads}
            columns={columns}
            rowKey="_id"
            loading={isLoading}
            emptyMessage="No leads found for the selected filters."
            onRowClick={(lead) => setSelectedLeadEmail(lead.email)}
          />
        </motion.div>

        {/* Server-side Pagination Controls */}
        <motion.div
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-4 sm:mt-6 px-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0 }}
        >
          <p className="text-xs sm:text-sm text-muted-foreground">
            {totalCount > 0 ? (
              <>
                <span className="font-semibold text-foreground">{((currentPage - 1) * pageSize) + 1}</span> to{" "}
                <span className="font-semibold text-foreground">{Math.min(currentPage * pageSize, totalCount)}</span> of{" "}
                <span className="font-semibold text-foreground">{totalCount.toLocaleString()}</span> row(s)
                {searchQuery && <span className="hidden sm:inline text-muted-foreground/60"> (filtered)</span>}
              </>
            ) : (
              <>0 of 0 row(s){searchQuery && <span className="hidden sm:inline text-muted-foreground/60"> (no matches)</span>}</>
            )}
          </p>
          <div className="flex items-center gap-3 sm:gap-6">
            {/* Rows per page selector - hidden on mobile */}
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Rows per page</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="h-9 w-[70px] rounded-lg border border-border bg-background px-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring/50 hover:border-border/80 cursor-pointer"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            {/* Page info */}
            <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
              <span className="font-semibold text-foreground">{currentPage}</span> / {" "}
              <span className="font-semibold text-foreground">{totalPages || 1}</span>
            </span>

            {/* Navigation buttons */}
            <div className="flex items-center gap-0.5 sm:gap-1">
              {/* First page - hidden on mobile */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="hidden sm:flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="First page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="h-8 w-8 sm:h-9 sm:w-9 flex items-center justify-center rounded-lg border border-border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="h-8 w-8 sm:h-9 sm:w-9 flex items-center justify-center rounded-lg border border-border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Next page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </motion.button>
              {/* Last page - hidden on mobile */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="hidden sm:flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Last page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Lead Timeline Drawer */}
      <AnimatePresence>
        {selectedLeadEmail && (
          <LeadTimelineDrawer
            email={selectedLeadEmail}
            onClose={() => setSelectedLeadEmail(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
